/**
 * Combined Punch Lists tour: list view → click a row → detail view.
 *
 * This is the primary tour for users who have at least one existing
 * punch list item — it walks the entire feature in one continuous run.
 * The transition step (``combinedTableStep``) clicks the first row,
 * navigates to the detail page, and waits for it to mount before the
 * detail steps take over. The TourProvider lives at the app root and
 * persists across route changes, so the tour state survives the
 * navigation seamlessly.
 *
 * For users with no items yet, see ``list.ts`` (list-only). For users
 * who land on a detail page without coming through this combined flow,
 * see ``detail.ts`` (detail-only). The auto-start logic in
 * ``PunchListsPage.tsx`` and ``PunchListDetail.tsx`` picks which one is
 * appropriate based on ``hasSeen`` + project state.
 */
import { registerTour } from '@/features/tours';
import { FEATURE_FLAGS } from '@/features/featureFlags';
import { PUNCH_LISTS_LIST_TOUR_ID } from './list';
import { PUNCH_LISTS_DETAIL_TOUR_ID } from './detail';
import {
  attachmentsStep,
  combinedTableStep,
  descriptionStep,
  filtersStep,
  headerActionsStep,
  metadataStep,
  newItemStep,
  peopleStep,
  statusChartStep,
} from './steps';

export const PUNCH_LISTS_COMBINED_TOUR_ID = 'punch_lists.combined';

registerTour({
  id: PUNCH_LISTS_COMBINED_TOUR_ID,
  name: 'Punch Lists',
  version: 1,
  featureFlagKey: FEATURE_FLAGS.PUNCH_LISTS.id,
  // Completing the combined tour also marks the list-only and
  // detail-only tours as seen — they cover the same content this tour
  // already walked through.
  alsoMarks: [PUNCH_LISTS_LIST_TOUR_ID, PUNCH_LISTS_DETAIL_TOUR_ID],
  steps: [
    {
      id: 'welcome',
      title: 'Welcome to Punch Lists',
      body:
        'Track outstanding work and quality issues across your project. ' +
        'We\u2019ll walk through the list view and then dive into one item.',
    },
    newItemStep,
    statusChartStep,
    filtersStep,
    combinedTableStep,
    headerActionsStep,
    metadataStep,
    descriptionStep,
    attachmentsStep,
    peopleStep,
  ],
});
